import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { API_URL } from "@/config";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Users, Link2, UserPlus, Loader2 } from "lucide-react";

const http = axios.create({ baseURL: API_URL, withCredentials: true });

interface StaffLink {
    label: string;
    path: string;
    description: string;
}

const staffLinks: StaffLink[] = [
    { label: "Add Salesperson", path: "/dealer/salesperson-create", description: "Create a new salesperson account for your dealership." },
    { label: "Add Sales Manager", path: "/dealer/salesmanager-create", description: "Add a sales manager who oversees salesperson activity." },
    { label: "Add Finance Manager", path: "/dealer/financemanager-create", description: "Finance manager handles credit apps and lender routing." },
    { label: "Add Service Manager", path: "/dealer/servicemanager-create", description: "Service manager oversees repair orders and service department." },
    { label: "Add BDC Agent", path: "/dealer/bdc-create", description: "Create a BDC rep (internal — for this dealership only)." },
];

export const DealerStaffPage = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const dealerId = Number(user?.dealerId ?? 0);

    const [bdcEmail, setBdcEmail] = useState("");
    const [linking, setLinking] = useState(false);
    const [linkResult, setLinkResult] = useState<{ ok: boolean; message: string } | null>(null);

    const linkBdc = async () => {
        if (!bdcEmail.trim() || !dealerId) return;
        setLinking(true);
        setLinkResult(null);
        try {
            await http.post(`/dealers/${dealerId}/bdc-links`, { email: bdcEmail.trim().toLowerCase() });
            setLinkResult({ ok: true, message: `BDC agent linked successfully.` });
            setBdcEmail("");
        } catch (e: any) {
            const msg = e?.response?.data?.error ?? e?.message ?? "Failed to link BDC agent.";
            setLinkResult({ ok: false, message: msg });
        } finally {
            setLinking(false);
        }
    };

    return (
        <div className="p-6 space-y-8">
            <h1 className="text-2xl font-light uppercase tracking-wide">Staff Management</h1>

            {/* Create Staff Cards */}
            <div>
                <h2 className="text-sm font-medium uppercase text-gray-400 mb-4">Create Staff</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {staffLinks.map(s => (
                        <div key={s.path} className="bg-white rounded-2xl shadow p-5 flex flex-col gap-3">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-indigo-50">
                                    <UserPlus className="h-5 w-5 text-indigo-600" />
                                </div>
                                <p className="font-medium text-sm">{s.label}</p>
                            </div>
                            <p className="text-xs text-gray-400 flex-1">{s.description}</p>
                            <Button
                                onClick={() => navigate(s.path)}
                                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white text-sm"
                            >
                                {s.label}
                            </Button>
                        </div>
                    ))}
                </div>
            </div>

            {/* Link External BDC Agent */}
            <div className="bg-white rounded-2xl shadow p-6 space-y-4">
                <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-purple-50">
                        <Link2 className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                        <h2 className="font-semibold">Link External BDC Agent</h2>
                        <p className="text-xs text-gray-400 mt-0.5">
                            Connect a multi-dealer BDC agent account to this dealership. They must already have a BDC account registered.
                        </p>
                    </div>
                </div>

                <div className="flex gap-3 max-w-md">
                    <Input
                        type="email"
                        placeholder="BDC agent email address"
                        value={bdcEmail}
                        onChange={e => setBdcEmail(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && linkBdc()}
                    />
                    <Button
                        onClick={linkBdc}
                        disabled={linking || !bdcEmail.trim()}
                        className="bg-purple-600 hover:bg-purple-700 text-white gap-2 whitespace-nowrap"
                    >
                        {linking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Users className="h-4 w-4" />}
                        Link Agent
                    </Button>
                </div>

                {linkResult && (
                    <p className={`text-sm ${linkResult.ok ? "text-green-600" : "text-red-500"}`}>
                        {linkResult.message}
                    </p>
                )}
            </div>
        </div>
    );
};

export default DealerStaffPage;
